import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { debounce } from 'lodash'
import { useFormikContext } from 'formik'
import { useBreakpoint } from '@mtsbank/ui-kit'
import { useCyberStore } from '@entities/tsp'
import { PaymentFormValues } from '@entities/payment'
import { reqHeaders } from '@shared/config'
import { Icon, ToolTipMobileText, ToolTipText } from '@shared/ui/atoms'
import { useTheme } from '@shared/lib'
import { CyberCourseSteam } from './CyberCourseSteam'
import { ICourseSteam } from '../model/payment.types'

const SteamFinalAccountAmount: FC = () => {
  const [isAmountCalculationModalActive, setIsAmountCalculationModalActive] = useState(false)
  const [loadingPrice, setLoadingPrice] = useState(false)
  const [courseSteam, setCourseSteam] = useState<ICourseSteam | null>({
    account_status: '',
    usd_topup: '',
    rub_topup: '',
    kzt_topup: '',
  })
  const [errorCourseSteam, setErrorCourseSteam] = useState(false)

  const { isMobile } = useBreakpoint()
  const theme = useTheme()

  const { values } = useFormikContext<PaymentFormValues>()

  const { activeCountrySteam } = useCyberStore()

  const iconAxiosRequest = useRef()
  const iconAxiosRequestAbortController = useRef(new AbortController())

  const debouncedHandleRequest = useCallback(
    debounce(
      (url, params, options: RequestInit) =>
        fetch(`${url}?${params}`, options)
          .then((response) => {
            if (!response.ok) {
              setErrorCourseSteam(true)
              setLoadingPrice(false)
            }

            return response.json()
          })
          .then((data: ICourseSteam) => {
            if (data.usd_topup === '0') {
              setCourseSteam(null)
              setLoadingPrice(false)

              return
            }

            setCourseSteam(data)
            setLoadingPrice(false)
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              setErrorCourseSteam(true)
              setLoadingPrice(false)
            }
          }),
      500
    ),
    []
  )

  const updatePriceSteam = (value: string) => {
    setLoadingPrice(true)
    if (iconAxiosRequest.current) {
      iconAxiosRequestAbortController.current.abort()
      iconAxiosRequestAbortController.current = new AbortController()
    }

    const url = 'https://keys.foreignpay.ru/webhook/v2/topup/check_rate'
    const params = new URLSearchParams({
      amount: String(value),
      account: values?.login || 'petrosovvput155',
    })

    const options = {
      method: 'GET',
      ...reqHeaders,
      signal: iconAxiosRequestAbortController.current.signal,
    }

    iconAxiosRequest.current = debouncedHandleRequest(url, params, options) as undefined
  }

  const updateValuePrice = (price: string) => {
    if (price !== null || price !== '') {
      updatePriceSteam(price)
    } else {
      setCourseSteam(null)
    }
  }

  useEffect(() => {
    updateValuePrice(values?.price)
  }, [values?.price])

  const handleOpenSteamCourse = () => {
    setIsAmountCalculationModalActive(true)
  }

  const handleCloseSteamCourse = () => {
    setIsAmountCalculationModalActive(false)
  }

  return (
    <CyberCourseSteam
      error={errorCourseSteam}
      courseSteam={courseSteam}
      loading={loadingPrice}
      label="К зачислению на Steam"
      activeCountrySteam={activeCountrySteam}
      tooltip={
        isMobile ? (
          <>
            <Icon
              width={24}
              height={24}
              onClick={handleOpenSteamCourse}
              icon="baseX24/question-circle"
              color={theme.colors.icon.tertiary}
            />
            <ToolTipMobileText
              open={isAmountCalculationModalActive}
              title="Как рассчитывается сумма к зачислению?"
              handleClose={handleCloseSteamCourse}
            >
              С&nbsp;учётом курсовой разницы на&nbsp;стороне Steam и&nbsp;провайдера услуг&nbsp;&mdash; до&nbsp;7%.
              Может меняться каждый час.
            </ToolTipMobileText>
          </>
        ) : (
          <ToolTipText
            interactive
            placement="bottom"
            zIndex={999}
            onHidden={() => {}}
            onClick={(e) => {
              e.stopPropagation()
            }}
            hint={
              <>
                С&nbsp;учётом курсовой разницы на&nbsp;стороне Steam и&nbsp;провайдера услуг&nbsp;&mdash; до&nbsp;7%.
                Может меняться каждый час.
              </>
            }
          />
        )
      }
    />
  )
}

export default SteamFinalAccountAmount
